// Dirt recipe — plan §4.2: fbm → ramp base, scatter(disc) pebbles over it (heightBlend),
// fbm(fine) → normal (detail relief). Immutable built-in root (never mutate — extend/fork instead).
//
// SCALE CONTRACT (the thing that made the first draft of this recipe read as noise):
// every length here is in METRES of world space, and the three layers must sit at clearly
// SEPARATED scales, coarse → fine, with height magnitudes ordered the same way:
//   base   ~6 m    colour only, no height  (macro mottling)
//   pebbles ~9 cm spacing, ~1.2 cm relief  (the read-as-dirt feature)
//   detail  ~6 cm cells,   ~1.5 mm relief  (grain; must NOT rival pebble relief)
// If the detail relief approaches the pebble relief, the detail normal swamps the pebble
// shading; if the base carried height, it would swamp both.
import {MacroModConfig, MaterialLayer, MaterialRecipe, ParamSchema} from "../@MaterialTypes.js";
import {colorRamp} from "../RecipeUtils.js";

export const DIRT_RECIPE_ID = "dirt";

export const dirtRecipe = new MaterialRecipe({
	id: DIRT_RECIPE_ID,
	name: "Dirt",
	roughness: 0.9,
	// Opt out of the specular lobe (2026-08-09, when roughnessAwareSpecular was enabled): dry dirt has
	// no coherent highlight, and 0 preserves this recipe's established pure-hillshade appearance.
	specStrength: 0,

	// ── Option D, first slice (2026-08-14): the MACRO MODULATOR.  Correct AA of a plain fbm is a CONSTANT (the fine
	// octaves are stationary), so wide zoom MUST flatten; varying the material's PARAMETERS with a slow field makes its
	// local MEAN vary, and a mean survives averaging (see Primitives/MacroMod.glsl.ts).  COARSE octaves (3 km→~170 m).
	macroMod: new MacroModConfig({
		// All fields are ParamRefs → the macro-mod FBM's own shape is live-tunable from the UI
		// (see the mod_* params below).  Defaults match the first-slice baked literals.
		wavelength: {ref: "mod_wavelength"}, octaves: {ref: "mod_octaves"},
		lacunarity: {ref: "mod_lacunarity"}, persistence: {ref: "mod_persistence"},
		contrast: {ref: "mod_contrast"}, seedOffset: {ref: "mod_seed"},
	}),

	params: [
		// ── Option D modulation-strength knobs (live sliders; ALL-ZERO = the pre-Option-D look).  Each targets a DIFFERENT
		// visual axis through the recipe's own ramps/thresholds (the point of Option D over a single global noise overlay);
		// experiment order: tone → pebble → grain.  Tone slides the base ramp lookup AND its fade target (rampMeanShifted).
		new ParamSchema({key: "mod_toneAmount", label: "[mod] Base tone swing", kind: "scalar", min: 0, max: 0.5, default: 0.5}),
		// Pebble presence: multiplicative on the per-cell spawn fraction (gravelly ground vs smooth soil) — gives the tone variation a REASON at close zoom.
		new ParamSchema({key: "mod_pebbleAmount", label: "[mod] Pebble presence swing", kind: "scalar", min: 0, max: 0.9, default: 0.55}),
		// Grain relief: multiplicative on the fine detail's height, so the micro-roughness (hillshade) varies too.
		new ParamSchema({key: "mod_grainAmount", label: "[mod] Grain relief swing", kind: "scalar", min: 0, max: 0.9, default: 0.45}),
		// ── The macro-mod FBM itself (Option D) — coarsest-octave wavelength (m), octave count, lacunarity,
		// persistence, output contrast, and the per-recipe lattice seed offset.  All live sliders.
		new ParamSchema({key: "mod_wavelength", label: "[mod] Wavelength (m)", kind: "scalar", min: 300, max: 30000, default: 3000}),
		new ParamSchema({key: "mod_octaves", label: "[mod] Octaves", kind: "scalar", min: 1, max: 8, default: 4}),
		new ParamSchema({key: "mod_lacunarity", label: "[mod] Lacunarity", kind: "scalar", min: 1.1, max: 5, default: 2.6}),
		new ParamSchema({key: "mod_persistence", label: "[mod] Persistence", kind: "scalar", min: 0.1, max: 0.9, default: 0.62}),
		new ParamSchema({key: "mod_contrast", label: "[mod] Contrast", kind: "scalar", min: 0.5, max: 6, default: 2.2}),
		new ParamSchema({key: "mod_seed", label: "[mod] Seed", kind: "scalar", min: -1000, max: 1000, default: 0}),

		new ParamSchema({key: "dirt_scale", label: "Base scale", kind: "scalar", min: 1, max: 40, default: 1}),
		new ParamSchema({key: "dirt_octaves", label: "Base octaves", kind: "scalar", min: 1, max: 8, default: 5}),
		new ParamSchema({key: "dirt_persistence", label: "Base persistence", kind: "scalar", min: 0.1, max: 0.9, default: 0.5}),
		// Base ramp re-centres on the turbulence mode's true mean (t ≈ 0.36 = value-noise |v| DC, NOT
		// 0.5): the old 0.5-midpoint ramp left the whole base in its bright tail at zoom-out (the
		// "too-light, cloudy" read). Stop 1 (t=0.36) = the dominant brown; the ramp mean ≈ mid-brown.
		// Base ramp re-centres on the turbulence mode's true mean (t ≈ 0.36 = value-noise |v| DC, NOT
		// 0.5): the old 0.5-midpoint ramp left the whole base in its bright tail at zoom-out (the
		// "too-light, cloudy" read). Stop 1 (t=0.36) = the dominant brown; the ramp mean ≈ mid-brown.
		new ParamSchema({key: "dirt_baseRamp", label: "Base ramp", kind: "ramp", default: colorRamp([
			/*{position: 0,   color_str: `rgb(69,48,28)`},   // darkest clump core
			{position: 0.36, color_str: `rgb(107,84,54)`},  // dominant brown (turbulence mean)
			{position: 1,   color_str: `rgb(153,122,82)`},  // highlight*/

			//{position: 0,   color_str: `rgb(70,50,30)`},   // darkest clump core
			//{position: 1, color_str: `rgb(73,52,31)`},  // dominant brown (turbulence mean)

			{position: 0,   color_str: `rgb(95,75,50)`},   // darkest clump core
			{position: 1, color_str: `rgb(115,87,55)`},  // dominant brown (turbulence mean)

			// Option D slice (2026-08-14) — WIDENED + a 3rd stop, a PREREQUISITE for judging the modulator: a tone shift is only
			// as visible as the ramp is wide, and the old 2-stop default spanned just ~20/12/5 of 255 (a full-swing modulator
			// would have looked like a failure).  Ends ≈ the original pair; the mid stop keeps the turbulence mean on the brown.
			/*{position: 0,    color_str: `rgb(74,56,36)`},    // damp/dark clump core
			{position: 0.36, color_str: `rgb(107,84,54)`},   // dominant brown (turbulence mean)
			{position: 1,    color_str: `rgb(146,118,82)`},*/  // dry/pale highlight
		])}),
		// Pebble SPACING in metres (the scatter lattice cell), not a count — 9 cm spacing gives
		// ~4 cm pebbles.  The old 0.6 default meant 60 cm spacing with ~27 cm radii: boulders,
		// which at close zoom filled the screen with a few huge grey blobs.
		new ParamSchema({key: "pebble_density", label: "Pebble spacing (m)", kind: "scalar", min: 0.005, max: 2, default: 0.015}),
		// Protrusion in metres.  Must stay well under the pebble radius or the dome's slope exceeds
		// 1 and the specular lobe blows out.  ~2 mm on a ~4 cm pebble (slope ~0.05).  The dome-slope
		// bound uses the MINIMUM radius: height < radiusMin * density (0.002 < 0.15 * 0.015).
		new ParamSchema({key: "pebble_height", label: "Pebble height (m)", kind: "scalar", min: 0, max: 0.5, default: 0.002}),
		// Pebbles are DIRT-toned (slightly lighter/greyer than the base), not the neutral mid-grey
		// the old default used — grey pebbles over brown dirt read as gravel-in-mud, not dirt.
		// Pebbles are DIRT-toned (slightly lighter/greyer than the base), not the neutral mid-grey
		// the old default used — grey pebbles over brown dirt read as gravel-in-mud, not dirt.
		new ParamSchema({key: "pebble_ramp", label: "Pebble ramp", kind: "ramp", default: colorRamp([
			{position: 0,   color_str: `rgb(107,89,69)`},   // dark pebble
			{position: 0.55, color_str: `rgb(133,115,92)`},  // mid pebble (dominant)
			{position: 1,   color_str: `rgb(158,143,120)`},  // light pebble
		])}),
		// Set3 §3.1 — pebble presence: the fraction of scatter cells that actually spawn a pebble.
		// 0.65 breaks the lattice rows (with presence 1 the per-cell grid IS the pebble row
		// structure — the zoomed-in "dots neatly packed into rows" read).
		new ParamSchema({key: "pebble_presence", label: "Pebble presence", kind: "scalar", min: 0.05, max: 1, default: 0.65}),
		// Softness of the heightBlend transition, in the same metres as pebble_height. Comparable to
		// pebble_height so coverage is a soft partial blend rather than a hard binary stamp.
		new ParamSchema({key: "pebble_blendSoftness", label: "Pebble blend softness", kind: "scalar", min: 0.001, max: 0.2, default: 0.02}),
		// Fine grain: cell size in metres. 1.2 m was coarser than the pebbles it was supposed to
		// texture BETWEEN, so it read as large-scale lumps fighting the base fbm instead of grain.
		new ParamSchema({key: "detail_scale", label: "Detail scale (m)", kind: "scalar", min: 0.01, max: 8, default: 0.06}),
		new ParamSchema({key: "detail_octaves", label: "Detail octaves", kind: "scalar", min: 1, max: 8, default: 3}),
		// Grain relief must be an order of magnitude below pebble_height, else the detail normal
		// swamps the pebble shading (and, pre-fix, drove the pebble heightBlend weight outright).
		new ParamSchema({key: "detail_height", label: "Detail relief (m)", kind: "scalar", min: 0, max: 0.2, default: 0.0015}),
		// Set3 §4.2 — the grain layer's persistence, previously a baked 0.5 literal; promoted to a param so the
		// Option D slice can leave it addressable from the UI (value unchanged → identical default look).
		new ParamSchema({key: "detail_persistence", label: "Detail persistence", kind: "scalar", min: 0.1, max: 0.9, default: 0.5}),
	],
	layers: [
		// Base: broad colour variation ONLY (contributes:"color").  It deliberately carries no
		// height: a metres-scale fbm's relief would dominate the height chain, and since the
		// pebbles' heightBlend weight is `pebbleH - hBelow`, any base relief comparable to
		// pebble_height would gate pebble colour by macro noise instead of by the pebbles.
		// Macro undulation is the TERRAIN's job (the geometric normal), not the material's.
		new MaterialLayer({
			id: "base",
			primitive: "fbm",
			args: {
				scale: {ref: "dirt_scale"},
				octaves: {ref: "dirt_octaves"},
				lacunarity: 2,
				persistence: {ref: "dirt_persistence"},
				mode: 3, // turbulence — abs-sum gives the clumped/mottled look of soil; plain fbm (0) is too smoothly cloudy
				//mode: 0, // I edited to this; maybe not best long-term, but better atm
				colorRamp: {ref: "dirt_baseRamp"},
				heightScale: 0,
				// Option D — the macro TONE shift: `add` (ramp positions live in [0,1]; base 0 so amount alone is the
				// swing, 0 = no-op).  NOTE: the ONE knob that works at any zoom — it moves the fade's TARGET colour
				// (rampMeanShifted), not just the per-fragment lookup, so it does not fade out with the noise it rides on.
				colorShift: {base: 0, amount: {ref: "mod_toneAmount"}, op: "add"},
			},
			blend: "over",
			contributes: "color",
		}),
		new MaterialLayer({
			id: "pebbles",
			primitive: "scatter",
			args: {
				density: {ref: "pebble_density"},
				shape: 0,
				aspect: 1,
				// Set3 §4.4 — rotation jitter is moot for discs; placement jitter keeps the
				// placement irregular while staying roughly one pebble per cell.
				rotJitter: 0,
				// FULL placement jitter (2026-08-08).  The Set3 §4.4 warning against 1.0 is about
				// ROTATED chips (mulch), where full-swing placement plus full rotation makes
				// neighbours collide; a disc has no rotation to fight, so the only effect here is
				// that the centre can reach anywhere in its cell instead of the middle 70%.  At 0.7
				// every pebble stayed inside a visible 0.7-cell box, so the lattice remained legible
				// as a grid even once the hash bug (below) was fixed.
				placeJitter: 1,
				// Set3 §4.1 — the lattice-row breaker (65% of cells spawn a pebble).
				// Option D — macro-modulated: `presence` is a per-cell hash THRESHOLD, so `mul` scales it smoothly
				// (gravelly ground vs smooth soil); >1 is harmless (shader just spawns every cell).
				presence: {base: {ref: "pebble_presence"}, amount: {ref: "mod_pebbleAmount"}, op: "mul"},
				// Radius range as a fraction of the cell.  Kept < 0.5 cell so pebbles never fuse into
				// rows (the Set3 row-band fix — see Scatter.glsl.ts for the real hash root cause).
				// The dome-slope bound uses the MINIMUM radius (steepest dome): 0.002 < 0.15 * 0.015.
				radiusMin: 0.15,
				radiusMax: 0.45,
				colorRamp: {ref: "pebble_ramp"},
				height: {ref: "pebble_height"},
				colorJitterSeed: 1,
			},
			blend: "heightBlend",
			blendArgs: {softness: {ref: "pebble_blendSoftness"}},
			contributes: "both",
		}),
		new MaterialLayer({
			id: "detail",
			primitive: "fbm",
			args: {
				scale: {ref: "detail_scale"},
				octaves: {ref: "detail_octaves"},
				lacunarity: 2,
				persistence: {ref: "detail_persistence"},
				mode: 0,
				// Option D — macro-modulated grain RELIEF: varies the micro-roughness (hillshade) so the tone change
				// reads as a real change of ground.  Feeds the DERIVATIVE body too; g_macroMod is hoisted above the
				// dChain call so the value and gradient agree.  `mul` on a height in metres.
				heightScale: {base: {ref: "detail_height"}, amount: {ref: "mod_grainAmount"}, op: "mul"},
			},
			blend: "over",
			contributes: "height",
		}),
	],
});
